// Admin set / change of an order's tracking number — pure rules.
// bo-api #931 (owner ruling 2026-10-15): PUT /api/v1/orders/tracking-attach/:id
// is open to admins (group 1 + SuperAdmin 99). Body { tracking_number, replace }.
// An order that already has a number answers 409 errors.order_has_tracking
// unless replace:true; a running booking pass / another change holds the
// round lock → 409 errors.tracking_busy; bad number → 400
// errors.tracking_number_invalid. Cancelled orders are allowed (warned here).

import { normalizeTrackingNumber, trackingNumberError } from './trackingFailure';

export const ORDER_STATUS_CANCELLED = 140;

export const ERR_ORDER_HAS_TRACKING = 'errors.order_has_tracking';
export const ERR_TRACKING_BUSY = 'errors.tracking_busy';
export const ERR_TRACKING_INVALID = 'errors.tracking_number_invalid';

/** The order's current number ('' when none). */
export function currentTrackingOf(order) {
  if (!order || typeof order !== 'object') return '';
  return String(order.shipping_tracking_number == null ? '' : order.shipping_tracking_number).trim();
}

export function isCancelledOrder(order) {
  return !!order && Number(order.order_status) === ORDER_STATUS_CANCELLED;
}

/**
 * What saving `input` on `order` means:
 * { tn, error, same, replacing, cancelled, current }.
 * error = i18n key ('' = ok); same = the new number equals the current one
 * (nothing to save); replacing = the order already has a number (send
 * replace:true after the admin confirmed).
 */
export function trackingChangePlan(order, input) {
  const current = currentTrackingOf(order);
  const tn = normalizeTrackingNumber(input);
  const error = trackingNumberError(input);
  return {
    tn,
    current,
    error,
    same: !error && current !== '' && current.toUpperCase() === tn,
    replacing: current !== '',
    cancelled: isCancelledOrder(order)
  };
}

/** The confirm text keys to show before saving (in order); [] = no confirm. */
export function trackingChangeConfirmKeys(plan) {
  const keys = [];
  if (!plan || plan.error || plan.same) return keys;
  if (plan.replacing) keys.push('tracking_change.confirm_replace');
  if (plan.cancelled) keys.push('tracking_change.confirm_cancelled');
  return keys;
}

/** The PUT body. */
export function trackingChangePayload(plan, forceReplace) {
  return { tracking_number: plan.tn, replace: !!(forceReplace || plan.replacing) };
}

/**
 * A 409 order_has_tracking on a request sent WITHOUT replace means the order
 * got a number meanwhile: ask the admin to confirm the overwrite and resend.
 */
export function shouldAskReplace(errorKey, sentReplace) {
  return errorKey === ERR_ORDER_HAS_TRACKING && !sentReplace;
}

/** The screen's wording for a refusal key ('' → caller falls back). */
export function trackingChangeErrorKey(key) {
  if (key === ERR_ORDER_HAS_TRACKING) return 'tracking_change.has_tracking';
  return key || '';
}
